// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

// CSS
import "./DesignJobs.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";
import { UserPreferencesContext } from "../../../context/UserPreferencesContext";

// MUI COMPONENTS
import {
	Button,
	ButtonGroup,
	CircularProgress,
	IconButton,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";
import SearchBar from "../../Misc/SearchBar/SearchBar";
import AddPhaseToDesignJobsDialog from "./AddPhaseToDesignJobsDialog/AddPhaseToDesignJobsDialog";
import ConfirmRemovePhaseDialog from "./ConfirmRemovePhaseDialog/ConfirmRemovePhaseDialog";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const DesignJobs = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [designJobsRecords, setDesignJobsRecords] = useState([]);

	const [designJobRows, setDesignJobRows] = useState([]);
	const [designJobColumns, setDesignJobColumns] = useState([]);

	// Table version and filters
	const [searchVersion, setSearchVersion] = useState(null); // set after prefs load
	const [resetAdvancedFilters, setResetAdvancedFilters] = useState(
		() => () => {}
	);

	// Add Phase dialog
	const [addPhaseDialogOpen, setAddPhaseDialogOpen] = useState(false);

	// Remove Phase confirmation
	const [removeTarget, setRemoveTarget] = useState(null);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);
	const {
		prefs,
		setPref,
		loading: prefsLoading,
	} = useContext(UserPreferencesContext);

	// USE EFFECT
	useEffect(() => {
		// Fetch Spec Library Records
		fetchDesignJobsRecords();

		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Design Jobs",
			employeeID: employeeID,
			notes: "Accessed the Design Jobs page",
		});
	}, []);

	useEffect(() => {
		createRowData();
		createColumnData();
	}, [designJobsRecords]);

	// when prefs load/change, sync initial view
	useEffect(() => {
		if (
			prefs &&
			typeof prefs.legacy_design_jobs_filter !== "undefined"
		) {
			setSearchVersion(prefs.legacy_design_jobs_filter ? 1 : 2);
		}
	}, [prefs]);

	useEffect(() => {
		if (searchVersion === 1) {
			resetAdvancedFilters();
			createRowData();
		} else if (searchVersion === 2) {
			createRowData();
		}
	}, [searchVersion]);

	// INPUT HANDLERS

	// API FUNCTIONS
	const fetchDesignJobsRecords = async () => {
		setIsLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/design/all",
				{ bqeStatus: "Completed" }
			);
			setDesignJobsRecords(res.data);
		} catch (error) {
			console.log("Error in fetchDesignJobsRecords: ", error);
		}

		setIsLoading(false);
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setDesignJobColumns([
			{
				accessorKey: "job_name",
				label: "Job Name",
				header: () => <span>Job Name</span>,
				cell: ({ row, getValue }) =>
					row.original.bqe_id ? (
						<RouterLink
							to={`/projects/viewer?recordId=${row.original.bqe_id}&tab=atAGlance`}
							style={{ fontWeight: 500 }}
						>
							{getValue() || notApplicable}
						</RouterLink>
					) : (
						<span>{getValue() || notApplicable}</span>
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "project_manager",
				label: "Project Manager",
				header: () => <span>Project Manager</span>,
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "oic",
				label: "OIC",
				header: () => <span>OIC</span>,
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "phase_dropbox_path",
				label: "Phase Dropbox Path",
				header: () => <span>Phase Dropbox Path</span>,
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				enableSorting: false,
			},
			{
				accessorKey: "created",
				label: "Created",
				header: () => <span>Created</span>,
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "actions",
				label: "Actions",
				header: () => <span />,
				cell: ({ row }) =>
					row.original.id ? (
						<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
							<Button
								size="small"
								variant="outlined"
								component={RouterLink}
								to={`/design/spec_books/${row.original.id}`}
							>
								Spec Book
							</Button>
							<Button
								size="small"
								variant="outlined"
								component={RouterLink}
								to={`/design/scope_of_work/${row.original.id}`}
							>
								Scope of Work
							</Button>
							{row.original.phase_type_id !== 2 &&
								row.original.phase_type_id !== 7 && (
									<Tooltip title="This phase was added manually. Click to remove it from Design Jobs.">
										<IconButton
											size="small"
											onClick={() =>
												setRemoveTarget(row.original)
											}
										>
											<RemoveCircleOutlineIcon
												color="error"
												fontSize="small"
											/>
										</IconButton>
									</Tooltip>
								)}
						</div>
					) : (
						notApplicable
					),
				enableSorting: false,
			},
		]);
	};

	const createRowData = (records = designJobsRecords) => {
		setDesignJobRows(
			records.map((record) => ({
				id: record.id,
				bqe_id: record.bqe_id || null,
				phase_type_id: record.phase_type_id,
				job_name: record.job_name,
				project_manager: record.project_manager,
				oic: record.oic,
				created: record.created ? convertToPST(record.created) : null,
				phase_dropbox_path:
					record.phase_dropbox_path ?? record.dropbox_path,
			}))
		);
	};

	const removePhaseFromDesignJobs = async (phaseID) => {
		try {
			await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/design/toggleInclude",
				{
					id: phaseID,
					include: 0,
					employeeID: employeeID,
				}
			);
			fetchDesignJobsRecords();
		} catch (error) {
			console.log("Error removing phase from Design Jobs: ", error);
		}
	};

	const removeConfirmed = () => {
		if (removeTarget) {
			removePhaseFromDesignJobs(removeTarget.id);
		}
		setRemoveTarget(null);
	};

	const filterDesignJobsRecords = (searchValue, searchByParameter) => {
		if (!searchValue || searchValue.trim() === "") {
			createRowData();
			return;
		}

		const normalized = searchValue.toLowerCase();
		const filteredRecords = designJobsRecords.filter((record) => {
			const value = record?.[searchByParameter];
			if (value === null || typeof value === "undefined") {
				return false;
			}
			return String(value).toLowerCase().includes(normalized);
		});

		createRowData(filteredRecords);
	};

	// RENDER
	return (
		<div className="DesignJobs">
			<div className="designJobsHeader">
				<div className="designJobsHeaderTitle">
					Design Jobs Archive
					<div className="designJobsHeaderButtons">
						<Button
							variant="contained"
							onClick={() => setAddPhaseDialogOpen(true)}
						>
							Add Phase
						</Button>
						{!prefsLoading && searchVersion !== null && (
							<ButtonGroup>
								<Button
									onClick={() => {
										setSearchVersion(1);
										// persist preference: true = legacy
										setPref(
											"legacy_design_jobs_filter",
											true
										);
									}}
									variant={
										searchVersion === 1
											? "contained"
											: "outlined"
									}
								>
									Legacy
								</Button>
								<Button
									onClick={() => {
										setSearchVersion(2);
										// persist preference: false = advanced
										setPref(
											"legacy_design_jobs_filter",
											false
										);
									}}
									variant={
										searchVersion === 2
											? "contained"
											: "outlined"
									}
								>
									Advanced
								</Button>
							</ButtonGroup>
						)}
					</div>
				</div>
			</div>
			<AddPhaseToDesignJobsDialog
				open={addPhaseDialogOpen}
				addPhaseToDesignJobsDialogClosed={() =>
					setAddPhaseDialogOpen(false)
				}
				fetchDesignJobsRecords={fetchDesignJobsRecords}
			/>
			<ConfirmRemovePhaseDialog
				open={!!removeTarget}
				jobName={removeTarget?.job_name}
				onCancel={() => setRemoveTarget(null)}
				onConfirm={removeConfirmed}
			/>
			{searchVersion === 1 && (
				<div className="legacyTableContainer">
					<SearchBar
						filterResults={(
							searchValue,
							searchByParameter
						) =>
							filterDesignJobsRecords(
								searchValue,
								searchByParameter
							)
						}
						searchByParameterMap={{
							job_name: "Job Name",
							project_manager: "Project Manager",
							oic: "OIC",
						}}
						searchDefaultValue="job_name"
					/>
				</div>
			)}
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{designJobsRecords.length === 0 && <J2NoResultsFound />}
					{designJobsRecords.length > 0 &&
						designJobColumns.length > 0 &&
						designJobRows.length > 0 && (
							<>
								<TanStackTable
									columnData={designJobColumns}
									rowData={designJobRows}
									showSearch={
										searchVersion === 1 ? false : true
									}
									setFilterResetter={setResetAdvancedFilters}
								/>
							</>
						)}
				</div>
			)}
		</div>
	);
};

// EXPORT
export default DesignJobs;
